import { useQueryClient } from '@tanstack/react-query';
import { ZfLoadingSpinner } from '@zafire/react';
import type { FunctionComponent } from 'react';
import { Suspense, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { showErrorToast, showSuccessToast } from '../../../app/utils/toastUtils';
import { useWebComponentEvents } from '../../hooks';
import { refetchFamilyTreeAfterRelationChange } from '../../utils/refetch-family-tree-after-relation-change';
import { PartnerRelationModalWidget } from './PcmPartnerRelationModalWidget';

interface IUpdatePartnerRelationModalProps {
    mainPartnerUuid: string;
    partnerUuid: string;
    relationshipManagerUserId?: string;
    onModalClose: () => void;
}

export const UpdatePartnerRelationModal: FunctionComponent<IUpdatePartnerRelationModalProps> = ({
    mainPartnerUuid,
    partnerUuid,
    relationshipManagerUserId,
    onModalClose,
}) => {
    const { t, i18n } = useTranslation();
    const queryClient = useQueryClient();

    const modalRef = useRef<HTMLElement>(null);
    const [isModalReady, setIsModalReady] = useState<boolean>(false);

    useWebComponentEvents(modalRef, isModalReady, [
        {
            eventName: 'PCM_UPDATE_PARTNER_RELATION_MODAL_CLOSE',
            eventCallback: () => {
                onModalClose();
                setIsModalReady(false);
            },
        },
        {
            eventName: 'PCM_UPDATE_PARTNER_RELATION_SUCCESS',
            eventCallback: () => {
                showSuccessToast(t('updatePartnerRelationSuccess'));
                refetchFamilyTreeAfterRelationChange(queryClient, mainPartnerUuid);
            },
        },
        {
            eventName: 'PCM_UPDATE_PARTNER_RELATION_ERROR',
            eventCallback: () => showErrorToast({ userMessage: t('updatePartnerRelationFailure') }),
        },
    ]);

    return createPortal(
        <Suspense fallback={<ZfLoadingSpinner style={{ margin: 'auto' }} />}>
            <PartnerRelationModalWidget
                ref={modalRef}
                modalType="UpdatePartnerRelation"
                language={i18n.language}
                partnerUuid={partnerUuid}
                relationshipManagerUserId={relationshipManagerUserId}
                onModalReady={() => setIsModalReady(true)}
            />
        </Suspense>,
        document.body,
    );
};
